/**
 * Sync Queue Operations
 * 
 * Provides operations for sync queue, sync metadata, and permissions
 * with proper offline handling and queue management.
 */

import { STORES } from '../schema';
import type { 
  SyncQueueItem, 
  SyncMetadata, 
  PermissionsOffline 
} from '../schema';
import { offlineDB } from '../database';

/**
 * Sync Queue Operations
 */
export class SyncQueueOperations {
  /**
   * Add item to sync queue
   */
  async add(item: Omit<SyncQueueItem, 'id' | 'created_at' | 'status' | 'retry_count' | 'max_retries' | 'priority'>): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: crypto.randomUUID(),
      table_name: item.table_name,
      record_id: item.record_id,
      operation: item.operation,
      data: item.data,
      priority: this.getSyncPriority(item.operation),
      retry_count: 0,
      max_retries: 3,
      created_at: new Date().toISOString(),
      status: 'pending'
    };

    await offlineDB.put(STORES.SYNC_QUEUE, queueItem);
  }

  /**
   * Get sync priority for operation type
   */
  private getSyncPriority(operation: 'create' | 'update' | 'delete'): number {
    switch (operation) {
      case 'create': return 2;
      case 'update': return 3;
      case 'delete': return 1; // Highest priority
      default: return 5;
    }
  }

  /**
   * Get all pending sync items
   */
  async getPendingItems(): Promise<SyncQueueItem[]> {
    try {
      return await offlineDB.queryByIndex<SyncQueueItem>(STORES.SYNC_QUEUE, 'by_status', 'pending');
    } catch (error) {
      // Fallback to manual filtering if index doesn't exist
      console.warn('by_status index not found, falling back to manual filtering:', error);
      const allItems = await offlineDB.getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
      return allItems.filter(item => item.status === 'pending');
    }
  }

  /**
   * Get sync items by priority
   */
  async getByPriority(priority: number): Promise<SyncQueueItem[]> {
    try {
      return await offlineDB.queryByIndex<SyncQueueItem>(STORES.SYNC_QUEUE, 'by_priority', priority);
    } catch (error) {
      // Fallback to manual filtering if index doesn't exist
      console.warn('by_priority index not found, falling back to manual filtering:', error);
      const allItems = await offlineDB.getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
      return allItems.filter(item => item.priority === priority);
    }
  }

  /**
   * Mark sync item as processing
   */
  async markAsProcessing(id: string): Promise<void> {
    const item = await offlineDB.get<SyncQueueItem>(STORES.SYNC_QUEUE, id);
    if (item) {
      item.status = 'processing';
      item.last_attempt = new Date().toISOString();
      await offlineDB.put(STORES.SYNC_QUEUE, item);
    }
  }

  /**
   * Mark sync item as completed
   */
  async markAsCompleted(id: string): Promise<void> {
    await offlineDB.delete(STORES.SYNC_QUEUE, id);
  }

  /**
   * Mark sync item as failed and increment retry count
   */
  async markAsFailed(id: string, errorMessage: string): Promise<void> {
    const item = await offlineDB.get<SyncQueueItem>(STORES.SYNC_QUEUE, id);
    if (item) {
      item.retry_count++;
      item.error_message = errorMessage;
      item.status = item.retry_count >= item.max_retries ? 'failed' : 'pending';
      await offlineDB.put(STORES.SYNC_QUEUE, item);
    }
  }

  /**
   * Clear completed and failed items
   */
  async clearProcessedItems(): Promise<void> {
    const allItems = await offlineDB.getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
    const toDelete = allItems.filter(item => item.status === 'completed' || item.status === 'failed');

    for (const item of toDelete) {
      await offlineDB.delete(STORES.SYNC_QUEUE, item.id);
    }
  }

  /**
   * Clear failed position sync items to allow retry with fixed transformer
   */
  async clearFailedPositionItems(): Promise<number> {
    const allItems = await offlineDB.getAll<SyncQueueItem>(STORES.SYNC_QUEUE);
    const failedPositionItems = allItems.filter(item =>
      item.table_name === 'positions' &&
      item.status === 'failed' &&
      item.error_message?.includes('parentId')
    );

    for (const item of failedPositionItems) {
      await offlineDB.delete(STORES.SYNC_QUEUE, item.id);
    }

    return failedPositionItems.length;
  }

  /**
   * Get sync queue statistics
   */
  async getStats(): Promise<{ pending: number; processing: number; failed: number }> {
    const allItems = await offlineDB.getAll<SyncQueueItem>(STORES.SYNC_QUEUE);

    return {
      pending: allItems.filter(item => item.status === 'pending').length,
      processing: allItems.filter(item => item.status === 'processing').length,
      failed: allItems.filter(item => item.status === 'failed').length
    };
  }
}

/**
 * Sync Metadata Operations
 */
export class SyncMetadataOperations {
  /**
   * Get sync metadata for a table
   */
  async getTableMetadata(tableName: string): Promise<SyncMetadata | null> {
    return offlineDB.get<SyncMetadata>(STORES.SYNC_METADATA, tableName);
  }

  /**
   * Update sync metadata for a table
   */
  async updateTableMetadata(metadata: SyncMetadata): Promise<void> {
    await offlineDB.put(STORES.SYNC_METADATA, metadata);
  }

  /**
   * Get all sync metadata
   */
  async getAllMetadata(): Promise<SyncMetadata[]> {
    return offlineDB.getAll<SyncMetadata>(STORES.SYNC_METADATA);
  }

  /**
   * Update last sync time for a table
   */
  async updateLastSync(tableName: string, timestamp: string): Promise<void> {
    const existing = await this.getTableMetadata(tableName);
    const metadata: SyncMetadata = {
      table_name: tableName,
      last_sync: timestamp,
      sync_strategy: existing?.sync_strategy || 'incremental',
      record_count: existing?.record_count || 0,
      ...(existing && { last_full_sync: existing.last_full_sync })
    };

    await this.updateTableMetadata(metadata);
  }
}

/**
 * Permissions Operations
 * Special operations for permissions caching with offline support
 */
export class PermissionsOperations {
  private storeName = STORES.PERMISSIONS;

  /**
   * Get cached permissions for a user
   */
  async getByUserId(userId: string): Promise<PermissionsOffline | null> {
    const permissions = await offlineDB.queryByIndex<PermissionsOffline>(
      this.storeName,
      'by_user_id',
      userId
    );

    // Return the most recent non-expired permissions
    const validPermissions = permissions.filter(p => {
      const expiresAt = new Date(p.expires_at);
      return expiresAt > new Date();
    });

    if (validPermissions.length === 0) return null;

    // Sort by cached_at descending and return the most recent
    validPermissions.sort((a, b) => new Date(b.cached_at).getTime() - new Date(a.cached_at).getTime());
    return validPermissions[0];
  }

  /**
   * Cache permissions for a user
   */
  async cachePermissions(
    userId: string,
    permissions: {
      is_authenticated: boolean;
      is_premium: boolean;
      is_admin: boolean;
      subscription_status: string;
      subscription_source: string;
      subscription_expires_at?: string;
      permissions_status: string;
      last_checked: number;
    },
    options: {
      ttl?: number; // Time to live in milliseconds
      source?: 'api' | 'fallback' | 'manual';
      invalidationReason?: string;
    } = {}
  ): Promise<PermissionsOffline> {
    const now = new Date();
    const ttl = options.ttl || (24 * 60 * 60 * 1000); // Default 24 hours
    const expiresAt = new Date(now.getTime() + ttl);

    const permissionsRecord: PermissionsOffline = {
      id: crypto.randomUUID(),
      user_id: userId,
      is_authenticated: permissions.is_authenticated,
      is_premium: permissions.is_premium,
      is_admin: permissions.is_admin,
      subscription_status: permissions.subscription_status as 'active' | 'inactive' | 'cancelled' | 'past_due' | 'trialing' | 'unpaid',
      subscription_source: permissions.subscription_source as 'stripe' | 'admin_override' | 'fallback',
      subscription_expires_at: permissions.subscription_expires_at,
      permissions_status: permissions.permissions_status as 'ready' | 'loading' | 'error',
      last_checked: permissions.last_checked,
      cached_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
      cache_source: options.source || 'api',
      invalidation_reason: options.invalidationReason,
      sync_required: false
    };

    await offlineDB.put(this.storeName, permissionsRecord);
    return permissionsRecord;
  }

  /**
   * Invalidate cached permissions for a user
   */
  async invalidateUserPermissions(userId: string, reason?: string): Promise<void> {
    const userPermissions = await offlineDB.queryByIndex<PermissionsOffline>(
      this.storeName,
      'by_user_id',
      userId
    );

    for (const permission of userPermissions) {
      // Mark as expired by setting expires_at to now
      permission.expires_at = new Date().toISOString();
      permission.invalidation_reason = reason || 'manual_invalidation';
      permission.sync_required = true;
      await offlineDB.put(this.storeName, permission);
    }
  }

  /**
   * Clear all expired permissions
   */
  async clearExpiredPermissions(): Promise<number> {
    const allPermissions = await offlineDB.getAll<PermissionsOffline>(this.storeName);
    const now = new Date();
    let clearedCount = 0;

    for (const permission of allPermissions) {
      const expiresAt = new Date(permission.expires_at);
      if (expiresAt <= now) {
        await offlineDB.delete(this.storeName, permission.id);
        clearedCount++;
      }
    }

    return clearedCount;
  }

  /**
   * Get all cached permissions (for debugging)
   */
  async getAllCachedPermissions(): Promise<PermissionsOffline[]> {
    return offlineDB.getAll<PermissionsOffline>(this.storeName);
  }

  /**
   * Check if user has valid cached permissions
   */
  async hasValidCachedPermissions(userId: string): Promise<boolean> {
    const permissions = await this.getByUserId(userId);
    return permissions !== null;
  }

  /**
   * Update sync requirement flag
   */
  async markSyncRequired(userId: string, required = true): Promise<void> {
    const permissions = await this.getByUserId(userId);
    if (permissions) {
      permissions.sync_required = required;
      await offlineDB.put(this.storeName, permissions);
    }
  }
}

// Export singleton instances
export const syncQueueOps = new SyncQueueOperations();
export const syncMetadataOps = new SyncMetadataOperations();
export const permissionsOps = new PermissionsOperations();
